/**
 * Showing a survey photo in the browser.
 *
 * A survey photo's stored `url` is the bare Storage download link (`...?alt=media`, no token): the server derives it from the
 * storage path on purpose, so no permanent bearer token is ever stored. storage.rules lets only a signed-in employee read that path,
 * and an <img> request carries no sign-in, so the bare link answers 403 and the picture is blank.
 *
 * The fix is to READ THE BYTES through the signed-in Storage SDK (getBlob: an authenticated read, subject to storage.rules) and draw
 * them from an object URL the tile revokes when it goes away. It is deliberately NOT getDownloadURL: on an object that has no download
 * token (the survey photos are uploaded by signed link and carry none; measured 0 of 41) the Storage backend MINTS and PERSISTS a
 * permanent bearer token the first time one is asked for, and that link then works for anyone who holds the string, forever.
 *
 * The package never talks to Firebase itself: each host registers one resolver at start-up (configureSurveyPhotoResolver).
 * With no resolver registered the stored url is used as is (the previous behaviour), so a host that has not opted in is unchanged.
 */

export type SurveyPhotoResolver = (storageRef: string) => Promise<Blob>;

/** organizations/<org>/surveys/<surveyId>/<file>: the only objects this module will ask the signed-in SDK for. */
export const SURVEY_PHOTO_REF = /^organizations\/[A-Za-z0-9_-]+\/surveys\/[A-Za-z0-9_-]+\/[^/]+$/;

/** https://firebasestorage.googleapis.com/v0/b/<bucket>/o/<encoded path>[?query] */
const DOWNLOAD_URL = /^https:\/\/firebasestorage\.googleapis\.com\/v0\/b\/[^/?#\s]+\/o\/([^?#\s]+)(?:\?([^#\s]*))?$/;

/**
 * The storage path behind a TOKENLESS download link, or null when the string is not one: not a Storage link, already carries a
 * `token` (it works as it is), or does not point at a survey photo path.
 */
export function storageRefFromTokenlessUrl(url: string | undefined | null): string | null {
  if (typeof url !== 'string') return null;
  const m = DOWNLOAD_URL.exec(url);
  if (!m) return null;
  if (m[2] && /(?:^|&)token=/.test(m[2])) return null;
  let path: string;
  try {
    path = decodeURIComponent(m[1]);
  } catch {
    return null;
  }
  return SURVEY_PHOTO_REF.test(path) ? path : null;
}

/**
 * Where a plain <img src> may point: a Storage host over https, or a local blob: / data:image/ preview. Anything else (another host,
 * http:, javascript:) is never handed to the browser as an image address.
 */
export function isDisplayableSrc(url: string): boolean {
  if (/^blob:/i.test(url) || /^data:image\//i.test(url)) return true;
  try {
    const u = new URL(url);
    return u.protocol === 'https:' && (u.hostname === 'firebasestorage.googleapis.com' || u.hostname === 'storage.googleapis.com');
  } catch {
    return false;
  }
}

/** At most this many photo reads run at once, so a survey with dozens of photos does not open dozens of connections on a phone. */
export const MAX_CONCURRENT_PHOTO_READS = 4;
/** Photos kept for re-draws (a tab switch, a second tile of the same photo): at most this many, and at most MAX_CACHED_BYTES. Oldest dropped first. */
export const MAX_CACHED_PHOTOS = 40;
export const MAX_CACHED_BYTES = 40 * 1024 * 1024;
/** A read that has not answered by now is given up on, so a few hung reads cannot hold every slot. (The SDK read itself cannot be cancelled.) */
export const PHOTO_READ_TIMEOUT_MS = 30_000;

let resolver: SurveyPhotoResolver | null = null;
// In-flight AND finished reads, by path: two tiles of one photo share ONE read. A rejected read is dropped, never remembered.
const reads = new Map<string, Promise<Blob>>();
const sizes = new Map<string, number>(); // bytes of the reads that have finished
let active = 0;
const queue: Array<() => void> = [];
const retryListeners = new Set<() => void>();
let retryGeneration = 0;

function fireRetry(): void {
  retryGeneration += 1;
  for (const l of [...retryListeners]) l();
}

/** Counts every "retry now" signal (back online, someone signed in). A tile that started loading before a signal and fails after it retries. */
export function getSurveyPhotoRetryGeneration(): number {
  return retryGeneration;
}

function drop(storageRef: string): void {
  reads.delete(storageRef);
  sizes.delete(storageRef);
}

function trim(keep: string): void {
  let total = 0;
  for (const n of sizes.values()) total += n;
  for (const key of [...reads.keys()]) {
    if (reads.size <= MAX_CACHED_PHOTOS && total <= MAX_CACHED_BYTES) break;
    if (key === keep) continue;
    total -= sizes.get(key) ?? 0;
    drop(key);
  }
}

/** Register how this host reads a storage path as a Blob (once, at start-up). Clears what was remembered. */
export function configureSurveyPhotoResolver(next: SurveyPhotoResolver | null): void {
  resolver = next;
  reads.clear();
  sizes.clear();
}

export function hasSurveyPhotoResolver(): boolean {
  return resolver !== null;
}

/** Forget every remembered photo (the signed-in user changed). Tiles on screen keep their own object URL until they unmount. */
export function clearSurveyPhotoCache(): void {
  reads.clear();
  sizes.clear();
}

/** Ask every tile that is showing "Tap to retry" to try again now (the connection came back, or someone signed in). */
export function notifySurveyPhotoRetry(): void {
  fireRetry();
}

/** A tile that failed subscribes to be told to retry: when the browser goes back online, or when notifySurveyPhotoRetry is called. */
export function onSurveyPhotoRetry(listener: () => void): () => void {
  if (retryListeners.size === 0 && typeof window !== 'undefined') window.addEventListener('online', fireRetry);
  retryListeners.add(listener);
  return () => {
    retryListeners.delete(listener);
    if (retryListeners.size === 0 && typeof window !== 'undefined') window.removeEventListener('online', fireRetry);
  };
}

/** The path to read for a photo, or null when the stored url can be used as it is. */
export function refToResolve(url: string | undefined | null, storageRef?: string | null): string | null {
  if (!resolver) return null;
  if (typeof storageRef === 'string' && SURVEY_PHOTO_REF.test(storageRef)) {
    return typeof url === 'string' && !storageRefFromTokenlessUrl(url) && /[?&]token=/.test(url) ? null : storageRef;
  }
  return storageRefFromTokenlessUrl(url);
}

/** Forget a remembered read (its image failed to draw, or the user asked to retry) so the next draw asks the host again. */
export function forgetResolved(storageRef: string): void {
  drop(storageRef);
}

/** Runs `task` once one of the MAX_CONCURRENT_PHOTO_READS slots is free. A finishing read hands its slot straight to the next waiter, so the cap is never exceeded. */
async function withSlot<T>(task: () => Promise<T>): Promise<T> {
  if (active >= MAX_CONCURRENT_PHOTO_READS) await new Promise<void>((go) => queue.push(go)); // the slot is handed to us: `active` stays counted
  else active += 1;
  try {
    return await task();
  } finally {
    const next = queue.shift();
    if (next) next();
    else active -= 1;
  }
}

function withTimeout<T>(p: Promise<T>): Promise<T> {
  return new Promise<T>((done, fail) => {
    const timer = setTimeout(() => fail(new Error('reading the photo timed out')), PHOTO_READ_TIMEOUT_MS);
    p.then(
      (v) => { clearTimeout(timer); done(v); },
      (e) => { clearTimeout(timer); fail(e); },
    );
  });
}

/** Read one path through the host's signed-in resolver. Rejects when no resolver is registered, the path is not a survey photo, the SDK refuses, or the read times out. */
export function resolveSurveyPhoto(storageRef: string): Promise<Blob> {
  const hit = reads.get(storageRef);
  if (hit) {
    reads.delete(storageRef); // re-insert: most recently used last
    reads.set(storageRef, hit);
    return hit;
  }
  if (!resolver) return Promise.reject(new Error('no survey photo resolver registered'));
  if (!SURVEY_PHOTO_REF.test(storageRef)) return Promise.reject(new Error('not a survey photo path'));
  const read = resolver;
  const p = withSlot(async () => {
    const blob = await withTimeout(read(storageRef));
    if (!(blob instanceof Blob) || blob.size === 0) throw new Error('the resolver returned no photo');
    return blob;
  });
  reads.set(storageRef, p);
  trim(storageRef);
  p.then(
    (blob) => {
      if (reads.get(storageRef) !== p) return; // forgotten, cleared or replaced while it was in flight
      sizes.set(storageRef, blob.size);
      trim(storageRef);
    },
    () => { if (reads.get(storageRef) === p) drop(storageRef); },
  );
  return p;
}
